/**
 * Firebase initialization for the web client.
 *
 * Firebase handles identity only. All application data comes from the
 * ChordBook Pro API, and Firestore is never used.
 */

import { initializeApp, getApps } from 'firebase/app';
import {
  getAuth,
  connectAuthEmulator,
  browserLocalPersistence,
  setPersistence,
} from 'firebase/auth';
import { firebaseConfig, isFirebaseConfigured, authEmulatorUrl } from '../config/appConfig.js';

let authInstance = null;

/**
 * Returns the Auth instance, or null when Firebase is not configured.
 *
 * Returning null rather than throwing lets the marketing site render fine on a
 * deployment that has no auth configured yet.
 */
export function getFirebaseAuth() {
  if (authInstance) return authInstance;

  if (!isFirebaseConfigured) {
    console.warn(
      'Firebase is not configured. Set VITE_FIREBASE_* in apps/web/.env — see docs/firebase.md.',
    );
    return null;
  }

  const app = getApps().length > 0 ? getApps()[0] : initializeApp(firebaseConfig);
  authInstance = getAuth(app);

  if (authEmulatorUrl) {
    connectAuthEmulator(authInstance, authEmulatorUrl, { disableWarnings: true });
  }

  // Keep the user signed in across tabs and reloads.
  setPersistence(authInstance, browserLocalPersistence).catch(() => {
    // Private browsing can block storage; the session simply won't persist.
  });

  return authInstance;
}

/** Current Firebase ID token, or null when signed out. */
export async function getIdToken(forceRefresh = false) {
  const auth = getFirebaseAuth();
  if (!auth?.currentUser) return null;

  try {
    return await auth.currentUser.getIdToken(forceRefresh);
  } catch {
    return null;
  }
}
